import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Heart, Eye, User, FlaskConical } from "lucide-react";

import { getAuth } from "firebase/auth";

import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";

import { db } from "../web/src/firebaseClient";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function ResearchProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 9;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const q = query(
          collection(db, "researchProjects"),
          where("status", "==", "approved")
        );

        const snapshot = await getDocs(q);

        const projectList = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          return {
            id: docSnap.id,
            projectTitle:
              data.projectTitle || data.title || "Başlıksız Proje",
            projectPurpose: data.projectPurpose || "",
            projectSummary: data.projectSummary || "",
            applicantName: data.applicantName || "",
            likeCount: data.likeCount || 0,
            createdAt: data.createdAt?.toDate?.() || null,
          };
        });

        setProjects(projectList);
      } catch (error) {
        console.error("Projeler alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const visibleProjects = useMemo(() => {
    let list = [...projects];

    if (sortBy === "newest") {
      list.sort(
        (a, b) =>
          (b.createdAt?.getTime?.() || 0) -
          (a.createdAt?.getTime?.() || 0)
      );
    }

    if (sortBy === "oldest") {
      list.sort(
        (a, b) =>
          (a.createdAt?.getTime?.() || 0) -
          (b.createdAt?.getTime?.() || 0)
      );
    }

    if (sortBy === "mostLiked") {
      list.sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0));
    }

    if (sortBy === "leastLiked") {
      list.sort((a, b) => (a.likeCount || 0) - (b.likeCount || 0));
    }

    return list;
  }, [projects, sortBy]);

  const totalPages = Math.ceil(visibleProjects.length / itemsPerPage);

  const paginatedProjects = visibleProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleLikeChange = (projectId, newCount) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, likeCount: newCount } : p
      )
    );
  };

  return (
    <main
      className="grid min-h-screen grid-rows-[auto_1fr_auto] bg-[#eef5ff]"
      style={{ fontFamily: "Poppins" }}
    >
      <InnovationHubHeader />

      <section className="px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-6">
        <div className="mx-auto max-w-[1600px]">
          <div className="mb-4 flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-sm sm:mb-6 sm:p-5 md:flex-row md:items-center md:gap-3">
            <div className="flex items-center gap-3">
              <Rocket className="shrink-0 text-[#1565C0]" />

              <div>
                <h1 className="text-xl font-semibold text-[#173b7a] sm:text-2xl">
                  Araştırma Projeleri
                </h1>
              </div>
            </div>

            <div className="flex w-full flex-col gap-2 sm:flex-row md:ml-auto md:w-auto md:items-center md:gap-3">
              <Link
                to="/my-research-projects"
                className="w-full rounded-full border border-[#1565C0] bg-white px-4 py-2 text-center text-sm text-[#1565C0] transition hover:bg-[#1565C0] hover:text-white sm:w-auto"
              >
                Projelerim
              </Link>

              <Link
                to="/research-project-application"
                className="w-full rounded-full bg-[#1565C0] px-4 py-2 text-center text-sm text-white transition hover:bg-[#0f57aa] sm:w-auto"
              >
                Başvur
              </Link>
            </div>
          </div>

          <section className="rounded-2xl bg-[#eaf2ff] p-4 shadow-sm sm:p-5">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#173b7a] sm:text-xl">
                  Projeler
                </h2>

                <p className="text-sm text-slate-500">
                  {visibleProjects.length} proje listeleniyor
                </p>
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-xl bg-white px-3 py-2 text-sm shadow-sm outline-none md:w-auto"
              >
                <option value="newest">Önce en yeni</option>
                <option value="oldest">Önce en eski</option>
                <option value="mostLiked">Önce en çok beğenilen</option>
                <option value="leastLiked">Önce en az beğenilen</option>
              </select>
            </div>

            {loading ? (
              <p>Yükleniyor...</p>
            ) : paginatedProjects.length === 0 ? (
              <div className="rounded-xl bg-white p-5 text-sm text-slate-500">
                Henüz proje bulunmuyor.
              </div>
            ) : (
              <div className="grid gap-4 sm:gap-5 md:grid-cols-2 md:gap-6 2xl:grid-cols-3">
                {paginatedProjects.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    onLikeChange={handleLikeChange}
                  />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.max(p - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="rounded-lg bg-white px-3 py-2 text-sm disabled:opacity-40"
                >
                  Önceki
                </button>

                <span className="text-sm">
                  {currentPage} / {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(p + 1, totalPages)
                    )
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-lg bg-white px-3 py-2 text-sm disabled:opacity-40"
                >
                  Sonraki
                </button>
              </div>
            )}
          </section>
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function ProjectCard({ project, onLikeChange }) {
  const auth = getAuth();
  const user = auth.currentUser;

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(project.likeCount || 0);

  useEffect(() => {
    const checkLiked = async () => {
      if (!user) return;

      const likeRef = doc(
        db,
        "researchProjects",
        project.id,
        "likes",
        user.uid
      );

      const snap = await getDoc(likeRef);

      setLiked(snap.exists());
    };

    checkLiked();
  }, [user, project.id]);

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) return;

    const projectRef = doc(db, "researchProjects", project.id);
    const likeRef = doc(projectRef, "likes", user.uid);

    if (liked) {
      await deleteDoc(likeRef);

      await updateDoc(projectRef, {
        likeCount: increment(-1),
      });

      const newCount = Math.max(likeCount - 1, 0);

      setLiked(false);
      setLikeCount(newCount);

      onLikeChange(project.id, newCount);
    } else {
      await setDoc(likeRef, {
        uid: user.uid,
        createdAt: serverTimestamp(),
      });

      await updateDoc(projectRef, {
        likeCount: increment(1),
      });

      const newCount = likeCount + 1;

      setLiked(true);
      setLikeCount(newCount);

      onLikeChange(project.id, newCount);
    }
  };

  return (
    <div className="group relative h-full overflow-hidden rounded-2xl border border-transparent bg-gradient-to-br from-[#3b82f6] via-[#f8fbff] to-[#ef4444] p-[1.5px] shadow-[0_12px_35px_rgba(21,101,192,0.18),0_8px_25px_rgba(198,40,40,0.12)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_45px_rgba(21,101,192,0.28),0_12px_35px_rgba(198,40,40,0.22)]">
      <div className="flex h-full flex-col rounded-2xl bg-gradient-to-br from-white via-[#f8fbff] to-[#fff1f1] p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#eef5ff] px-3 py-1.5 text-xs font-semibold text-[#1565C0] shadow-sm">
            <FlaskConical size={14} />
            Araştırma Çıktısı(White Paper)
          </div>

          {user ? (
            <button
              type="button"
              onClick={handleLike}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                liked
                  ? "bg-red-500 text-white shadow-md"
                  : "bg-transparent text-red-500 hover:bg-red-50"
              }`}
            >
              <Heart size={18} fill={liked ? "#fff" : "#ef4444"} />
              {likeCount}
            </button>
          ) : (
            <div className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold text-red-500">
              <Heart size={18} fill="#ef4444" />
              {likeCount}
            </div>
          )}
        </div>

        <h3 className="text-base font-bold leading-7 text-[#0f2f66] transition group-hover:text-[#1565C0] sm:min-h-[56px] sm:text-lg">
          {project.projectTitle}
        </h3>

        <div className="mt-4 space-y-3">
          <p className="text-sm leading-6 text-slate-700">
            <b className="font-bold text-[#0f2f66]">Özet:</b>{" "}
            {project.projectSummary || "Belirtilmemiş"}
          </p>
        </div>

       <div className="mt-auto pt-5">
        <div className="mb-4 h-px w-full bg-slate-200" />

        <div className="flex flex-col items-start gap-3">
          <div className="flex min-w-0 items-center gap-2 text-sm text-[#0f2f66]">
            <User size={17} className="shrink-0" />

            <span className="truncate">
              <b>Proje Sahibi:</b>{" "}
              {project.applicantName || "Belirtilmemiş"}
            </span>
          </div>

          <Link
            to={`/research-papers/${project.id}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[#1565C0] transition hover:text-[#0f57aa] hover:underline"
          >
            <Eye size={16} />
            Araştırma Çıktısını (White Paper) Oku
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}